import moment from 'moment';
import {
  SCREENING_SECTIONS,
  MDS_COMMUNICATION_FORM,
  otherKeyFor,
  type ScreeningField,
  type ScreeningSection,
} from './screeningFields';

// JSON Forms data schema for the screening form, generated from SCREENING_SECTIONS so it
// stays in lock-step with the UI schema. The data is grouped by section (one nested object
// per collapsible section) which keeps the eventual submission payload self-describing.

const propertyForField = (field: ScreeningField) => {
  switch (field.control) {
    case 'checkboxRow':
      // Multi-select -> array of enum strings (rendered by CheckboxButtonRow).
      // A required checkbox enforces at least one selection via minItems; a non-required one
      // defaults to an empty array. (minItems + a default of [] would always fail, so we only
      // set one or the other.)
      return {
        type: 'array',
        uniqueItems: true,
        items: { type: 'string', enum: field.options ?? [] },
        ...(field.required ? { minItems: 1 } : { default: [] }),
      };
    case 'buttonRow':
      // Labelled options (the GG scales) render their title on the chip, as the evaluations'
      // `$defs.ggAssistanceLevels` does; Reform's ButtonRow reads titles from `oneOf` only.
      if (field.optionLabels) {
        const labels = field.optionLabels;
        return {
          type: 'string',
          oneOf: (field.options ?? []).map((value) => ({
            const: value,
            title: labels[value] ?? value,
          })),
        };
      }
      return { type: 'string', enum: field.options ?? [] };
    case 'radio':
      return { type: 'string', enum: field.options ?? [] };
    case 'date':
      return { type: 'string', format: 'date', default: moment().format('YYYY-MM-DD') };
    case 'textarea':
    case 'text':
    default:
      return { type: 'string' };
  }
};

const buildSectionSchema = (section: ScreeningSection) => {
  const sectionProperties: Record<string, unknown> = {};
  const required: string[] = [];

  section.fields.forEach((field) => {
    sectionProperties[field.key] = propertyForField(field);
    if (field.required) required.push(field.key);
    // Companion free-text for an "Other" selection.
    if (field.allowOther) sectionProperties[otherKeyFor(field.key)] = { type: 'string' };
  });

  return { schema: { type: 'object', properties: sectionProperties }, required };
};

// Screening Type = "MDS Communication Form" (matches the section-visibility conditions).
const isMdsCondition = {
  properties: {
    basicDetails: {
      properties: { screeningType: { const: MDS_COMMUNICATION_FORM } },
      required: ['screeningType'],
    },
  },
  required: ['basicDetails'],
};

// Require a conditionally-visible section's fields only while that section is actually shown, so
// a hidden section's required fields never block submission (matches the Jotform, which doesn't
// validate hidden fields). Requiring the section object itself makes the requirement fire even
// before the user has touched anything in it.
const conditionalRequirement = (section: ScreeningSection, required: string[]) => {
  const requireBlock = {
    required: [section.id],
    properties: { [section.id]: { required } },
  };
  return section.showWhen === 'mdsOnly'
    ? { if: isMdsCondition, then: requireBlock }
    : { if: isMdsCondition, then: {}, else: requireBlock };
};

const properties: Record<string, unknown> = {};
const allOf: unknown[] = [];
const conditionallyRequiredSectionIds: string[] = [];

SCREENING_SECTIONS.forEach((section) => {
  const { schema: sectionSchema, required } = buildSectionSchema(section);
  if (required.length) {
    if (section.showWhen) {
      // Conditionally-visible section — gate its required fields on Screening Type.
      allOf.push(conditionalRequirement(section, required));
      conditionallyRequiredSectionIds.push(section.id);
    } else {
      // Always visible — require unconditionally.
      (sectionSchema as { required?: string[] }).required = required;
    }
  }
  properties[section.id] = sectionSchema;
});

export const schema = {
  type: 'object',
  properties,
  ...(allOf.length ? { allOf } : {}),
};

type SectionData = Record<string, unknown>;

const isEmptyObject = (value: unknown) =>
  !!value && typeof value === 'object' && !Array.isArray(value) && !Object.keys(value).length;

/**
 * BRK-2151 — seed each conditionally-required section with an empty object. Without it, an
 * untouched section fails as `/ must have required property '<section>'` at the root, which no
 * control owns: no field turns red, the collapsed section's header shows no error count, and the
 * user has no idea why Submit is disabled. With the object present, the error lands on the
 * missing field itself (e.g. `evaluationRecommendation`), which the section header counts.
 */
export const withSeededSections = (data: SectionData = {}): SectionData => {
  const seeded = { ...data };
  conditionallyRequiredSectionIds.forEach((id) => {
    if (seeded[id] === undefined) seeded[id] = {};
  });
  return seeded;
};

/**
 * Drop any seeded section the user never filled in (e.g. Screening Questions on an MDS
 * Communication Form, where it's hidden), so the submission payload is the same as before seeding.
 */
export const withoutEmptySeededSections = (data: SectionData): SectionData => {
  const stripped = { ...data };
  conditionallyRequiredSectionIds.forEach((id) => {
    if (isEmptyObject(stripped[id])) delete stripped[id];
  });
  return stripped;
};
